import type { PlatformService } from './types'

/**
 * Handle для отложенного открытия URL в окне/вкладке.
 *
 * Использование: вызвать `prepareAsyncOpenTab(platform)` СИНХРОННО в обработчике
 * клика — пока user-gesture жив. Затем выполнить await/мутацию и вызвать
 * `complete(url)` с полученным URL. Если что-то пошло не так — `abort()`.
 *
 * В web синхронно открывает `about:blank` через `window.open`, на `complete`
 * подставляет реальный URL через `location.replace`. Если popup всё же был
 * заблокирован — `complete` падает на `platform.openLink(url)`.
 *
 * В TG (и серверном/неподдерживаемом окружении) ничего не открывает заранее;
 * `complete` идёт сразу через `platform.openLink(url)` — там нативного
 * popup-блокировщика нет.
 */
export interface AsyncOpenTabHandle {
  /** Подставить реальный URL после async-операции. */
  complete: (url: string) => void
  /** Закрыть/отменить (например, при ошибке async-операции). */
  abort: () => void
}

export const prepareAsyncOpenTab = (
  platform: PlatformService
): AsyncOpenTabHandle => {
  // В TG (и SSR) не открываем заранее — там popup-блокировщика нет, а
  // нативный WebApp.openLink сам обрабатывает переход в Telegram.
  if (!platform.isWeb() || typeof window === 'undefined') {
    return {
      complete: (url: string) => platform.openLink(url),
      abort: () => {},
    }
  }

  // Web: открываем синхронно, пока активен user-gesture от клика.
  // Без noopener, иначе window.open вернёт null и нельзя будет выставить
  // location — для OAuth-подобного флоу это стандартное компромиссное решение.
  const popup = window.open('about:blank', '_blank')

  return {
    complete: (url: string) => {
      if (popup && !popup.closed) {
        popup.location.replace(url)
      } else {
        // Окно заблокировано браузером или закрыто пользователем —
        // пробуем штатный фолбэк.
        platform.openLink(url)
      }
    },
    abort: () => {
      if (popup && !popup.closed) popup.close()
    },
  }
}
